/**
 * CrewSchedulePage
 * ----------------
 * Drag-and-drop dispatch board. Rows are crew (roster + trade-contractor
 * portal users), columns are the seven days of the week. Drag a job card to
 * another day to reschedule it; drop it on another crew row to reassign.
 * Dispatching emails every assignee their job card.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, ChevronLeft, ChevronRight, Send, Trash2, Users, CalendarDays,
  AlertTriangle, CheckCircle2, Pencil, HardHat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, useAuth } from "@/App";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_STYLE = {
  scheduled: "bg-slate-100 text-slate-600 ring-slate-300",
  dispatched: "bg-blue-50 text-blue-700 ring-blue-300",
  in_progress: "bg-amber-50 text-amber-700 ring-amber-300",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-300",
  cancelled: "bg-slate-100 text-slate-400 ring-slate-200",
};

const shortDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const blankJob = (date) => ({
  title: "", job_date: date, start_time: "07:00", end_time: "15:30",
  project_id: "", project_name: "", location: "", trade: "general",
  scope: "", icra_permit_number: "", notes: "", assignees: [],
});

const blankMember = () => ({
  name: "", trade: "general", role: "", phone: "", email: "",
  company: "Sterile Mate LLC", certifications: "", active: true, notes: "",
});

const Stat = ({ label, value, accent = "text-slate-900", sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">{label}</p>
    <p className={`text-lg font-black mt-1 ${accent}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p> : null}
  </div>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">{children}</label>
);

export default function CrewSchedulePage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [weekStart, setWeekStart] = useState(null);
  const [data, setData] = useState(null);
  const [crew, setCrew] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [job, setJob] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [member, setMember] = useState(null);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [showRoster, setShowRoster] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [picked, setPicked] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerProjects, setPickerProjects] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [expandedProject, setExpandedProject] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, meta] = await Promise.all([
        axios.get(`${API}/crew/schedule${weekStart ? `?start=${weekStart}` : ""}`, { headers }),
        axios.get(`${API}/crew/members`, { headers }),
        axios.get(`${API}/crew/meta`, { headers }),
      ]);
      setData(s.data);
      setWeekStart(s.data.week_start);
      setCrew(m.data.crew || []);
      setContractors(m.data.contractors || []);
      setTrades(meta.data.trades || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the dispatch board");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/projects`, { headers })
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data?.projects || []))
      .catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const roster = useMemo(() => [...crew, ...contractors].filter((r) => r.active !== false),
    [crew, contractors]);

  const shiftWeek = (delta) => {
    const d = new Date(`${data.week_start}T12:00:00`);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const cell = (personId, date) =>
    (data?.assignments || []).filter(
      (a) => a.job_date === date && (a.assignees || []).some((x) => x.id === personId));

  const unassigned = (date) =>
    (data?.assignments || []).filter(
      (a) => a.job_date === date && !(a.assignees || []).some((x) => x.id));

  // --- schedule from a live project -------------------------------------
  const openPicker = async () => {
    setShowPicker(true);
    setPickerLoading(true);
    try {
      const r = await axios.get(`${API}/crew/schedulable-projects`, { headers });
      setPickerProjects(r.data.projects || []);
      setExpandedProject((r.data.projects || [])[0]?.id || null);
    } catch {
      toast.error("Could not load the live projects");
    } finally { setPickerLoading(false); }
  };

  const startFromProject = (project, phase = null) => {
    const firstDay = data?.dates?.[0] || new Date().toISOString().slice(0, 10);
    const jobDate = (phase?.start_date || "").slice(0, 10) || firstDay;
    const icra = phase?.infection_prevention?.icra_class &&
      phase.infection_prevention.icra_class !== "none"
      ? `ICRA ${phase.infection_prevention.icra_class.replace(/_/g, " ")}` : "";
    setEditingId(null);
    setJob({
      ...blankJob(jobDate),
      title: phase ? phase.name : project.name,
      project_id: project.id,
      project_name: project.name,
      location: project.facility_name || project.site || project.hospital_name || "",
      trade: (phase?.compliance_tags || []).find((t) => trades.includes(t)) || "general",
      scope: phase?.scope_notes || phase?.description || "",
      icra_permit_number: icra,
      notes: phase?.owner_name ? `Phase owner: ${phase.owner_name}` : "",
    });
    setShowPicker(false);
  };

  const startStandalone = () => {
    setEditingId(null);
    setJob(blankJob(data?.dates?.[0] || new Date().toISOString().slice(0, 10)));
    setShowPicker(false);
  };

  const saveJob = async () => {
    if (!job.title.trim()) return toast.error("Give the job a title");
    if (!job.assignees.length) return toast.error("Assign at least one crew member");
    setSaving(true);
    try {
      if (editingId) await axios.put(`${API}/crew/assignments/${editingId}`, job, { headers });
      else await axios.post(`${API}/crew/assignments`, job, { headers });
      toast.success(`${job.title} scheduled for ${job.job_date}`);
      setJob(null); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the job");
    } finally { setSaving(false); }
  };

  const notify = async (a) => {
    try {
      const r = await axios.post(`${API}/crew/assignments/${a.id}/notify`, {}, { headers });
      if (r.data.sent?.length) {
        toast.success(`Crew notified (${r.data.sent.length})`, {
          description: r.data.sent.join(", "),
        });
      } else {
        toast.error("Nobody was notified", {
          description: (r.data.failed || []).map((f) => f.email).join(", "),
        });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    }
  };

  const removeJob = async (a) => {
    if (!window.confirm(`Delete "${a.title}" on ${a.job_date}?`)) return;
    try {
      await axios.delete(`${API}/crew/assignments/${a.id}`, { headers });
      toast.success("Job removed"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const drop = async (date, personId) => {
    const a = dragging || picked;
    if (!a) return;
    setDragging(null);
    setPicked(null);
    const sameDay = a.job_date === date;
    const alreadyOn = (a.assignees || []).some((x) => x.id === personId);
    if (sameDay && (alreadyOn || !personId)) return;

    const body = { job_date: date };
    if (personId && !alreadyOn) {
      const person = roster.find((p) => p.id === personId);
      body.assignees = [{
        kind: person.kind, id: person.id, name: person.name,
        email: person.email || "", phone: person.phone || "", company: person.company || "",
      }];
    }
    try {
      await axios.put(`${API}/crew/assignments/${a.id}`, body, { headers });
      toast.success(`${a.title} moved to ${shortDate(date)}`, {
        description: body.assignees ? `Reassigned to ${body.assignees[0].name}` : undefined,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move the job");
    }
  };

  const saveMember = async () => {
    if (!member.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editingMemberId) await axios.put(`${API}/crew/members/${editingMemberId}`, member, { headers });
      else await axios.post(`${API}/crew/members`, member, { headers });
      toast.success(`${member.name} saved`);
      setMember(null); setEditingMemberId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the crew member");
    } finally { setSaving(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the roster?`)) return;
    try {
      await axios.delete(`${API}/crew/members/${m.id}`, { headers });
      toast.success("Removed from roster"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  const toggleAssignee = (person) => setJob((j) => {
    const on = (j.assignees || []).some((a) => a.id === person.id);
    return {
      ...j,
      assignees: on ? j.assignees.filter((a) => a.id !== person.id)
        : [...(j.assignees || []), {
            kind: person.kind, id: person.id, name: person.name,
            email: person.email || "", phone: person.phone || "", company: person.company || "",
          }],
    };
  });

  if (loading && !data) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-10">
      <Loader2 className="w-4 h-4 animate-spin" />Loading the dispatch board…</p>;
  }

  const Card = ({ a }) => (
    <div
      draggable
      onDragStart={() => setDragging(a)}
      onDragEnd={() => setDragging(null)}
      onClick={() => setPicked(picked?.id === a.id ? null : a)}
      className={`rounded-md px-2 py-1.5 mb-1 cursor-grab active:cursor-grabbing text-white
                 shadow-sm hover:brightness-110 transition-all ${
                   picked?.id === a.id ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
      style={{ backgroundColor: a.status === "cancelled" ? "#94a3b8" : a.color }}
      data-testid={`crew-job-${a.id}`}
      title={`${a.title} · ${a.start_time}–${a.end_time}`}
    >
      <p className="text-[11px] font-bold leading-tight truncate">{a.title}</p>
      <p className="text-[10px] opacity-90">{a.start_time}–{a.end_time}</p>
      <div className="flex items-center gap-1 mt-1">
        {a.has_conflict ? <AlertTriangle className="w-3 h-3 text-yellow-200" /> : null}
        {a.notified ? <CheckCircle2 className="w-3 h-3 text-emerald-200" /> : null}
        <button className="ml-auto text-[10px] underline decoration-white/50 hover:decoration-white"
          onClick={(e) => { e.stopPropagation(); setEditingId(a.id); setJob({ ...blankJob(a.job_date), ...a }); }}>
          open
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="crew-schedule-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span className="inline-block w-1 h-6 rounded bg-blue-600" />
            Crew Schedule &amp; Dispatch
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Drag a job to another day to reschedule it, or onto another crew row to reassign it — or tap
            a card then tap the cell you want it in. Dispatch emails each assignee their job card.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowRoster(true)}
            data-testid="crew-roster-btn">
            <Users className="w-4 h-4 mr-2" />Crew roster ({crew.length})
          </Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={openPicker}
            data-testid="new-crew-job-btn">
            <Plus className="w-4 h-4 mr-2" />Schedule a job
          </Button>
        </div>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="crew-summary">
          <Stat label="Jobs this week" value={data.summary.jobs}
            sub={`${data.summary.crew_booked} crew booked`} />
          <Stat label="Dispatched" value={data.summary.dispatched} accent="text-blue-700" />
          <Stat label="Not yet notified" value={data.summary.unnotified}
            accent={data.summary.unnotified ? "text-amber-600" : "text-slate-900"} />
          <Stat label="Double-booked" value={data.summary.conflicts}
            accent={data.summary.conflicts ? "text-red-600" : "text-emerald-600"}
            sub="Overlapping call times" />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => shiftWeek(-1)} data-testid="crew-prev-week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-slate-400" />
            {shortDate(data.week_start)} – {shortDate(data.week_end)}
          </span>
          <Button size="sm" variant="outline" onClick={() => shiftWeek(1)} data-testid="crew-next-week">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(null)}
            className="text-slate-500" data-testid="crew-this-week">This week</Button>
        </div>
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : null}
      </div>

      {picked && (
        <div className="rounded-lg bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="crew-move-banner">
          <p className="text-sm">
            Moving <b>{picked.title}</b> — click the day and crew you want it on.
          </p>
          <Button size="sm" variant="outline" className="text-slate-900"
            onClick={() => setPicked(null)} data-testid="crew-move-cancel">Cancel</Button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto shadow-sm">
        <table className="w-full min-w-[1180px] border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500 w-[190px]">
                Crew
              </th>
              {(data?.dates || []).map((d, i) => (
                <th key={d} className="text-left px-2 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-500">
                  {DAY_NAMES[i]} <span className="text-slate-400 font-bold">{shortDate(d)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roster.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                <HardHat className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                No crew on the roster yet — add your field staff, or assign a trade-contractor portal user.
              </td></tr>
            ) : roster.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 align-top">
                <td className="px-3 py-2">
                  <p className="text-sm font-bold text-slate-900 leading-tight">{p.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {(p.trade || "general").replace(/_/g, " ")}
                    {p.kind === "contractor" ? " · portal" : ""}
                  </p>
                  {p.phone ? <p className="text-[11px] text-slate-400">{p.phone}</p> : null}
                </td>
                {(data?.dates || []).map((d) => (
                  <td key={d}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => drop(d, p.id)}
                    onClick={() => picked && drop(d, p.id)}
                    className={`px-2 py-2 min-w-[140px] transition-colors ${
                      picked ? "cursor-copy bg-blue-50/60 hover:bg-blue-100" : "hover:bg-blue-50/40"}`}
                    data-testid={`crew-cell-${p.id}-${d}`}>
                    {cell(p.id, d).map((a) => <Card key={a.id} a={a} />)}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-slate-50/60">
              <td className="px-3 py-2 text-[11px] font-black uppercase tracking-wider text-slate-500">
                Unassigned
              </td>
              {(data?.dates || []).map((d) => (
                <td key={d}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => drop(d, null)}
                  onClick={() => picked && drop(d, null)}
                  className={`px-2 py-2 transition-colors ${
                    picked ? "cursor-copy bg-blue-50/60 hover:bg-blue-100" : "hover:bg-blue-50/40"}`}
                  data-testid={`crew-cell-unassigned-${d}`}>
                  {unassigned(d).map((a) => <Card key={a.id} a={a} />)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Week list with dispatch actions */}
      {(data?.assignments || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100"
          data-testid="crew-job-list">
          {data.assignments.map((a) => (
            <div key={a.id} className="p-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900">
                  <span className="inline-block w-2 h-2 rounded-full mr-2"
                    style={{ backgroundColor: a.color }} />
                  {a.title}
                  <span className="ml-2 text-xs font-semibold text-slate-500">
                    {shortDate(a.job_date)} · {a.start_time}–{a.end_time}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {[a.project_name, a.location,
                    (a.assignees || []).map((x) => x.name).join(", ")].filter(Boolean).join(" · ") || "—"}
                </p>
                {a.has_conflict ? (
                  <p className="text-[11px] text-red-700 font-bold mt-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Overlaps {a.conflicts.map((c) => c.title).join(", ")}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ring-1 ${STATUS_STYLE[a.status]}`}>
                  {(a.status || "").replace(/_/g, " ")}
                </span>
                <Button size="sm" variant="ghost" className="text-blue-700 hover:bg-blue-50"
                  onClick={() => notify(a)} data-testid={`crew-notify-${a.id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" />{a.notified ? "Re-send" : "Dispatch"}
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                  onClick={() => { setEditingId(a.id); setJob({ ...blankJob(a.job_date), ...a }); }}
                  data-testid={`crew-edit-${a.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                  onClick={() => removeJob(a)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project picker — a job is scheduled from a live project */}
      <Dialog open={showPicker} onOpenChange={(o) => !o && setShowPicker(false)}>
        <DialogContent className="max-w-2xl" data-testid="crew-project-picker">
          <DialogHeader>
            <DialogTitle>Schedule from a project</DialogTitle>
            <DialogDescription>
              Pick the project — or the exact phase — this crew is going out on. The job is
              pre-filled from it; you just add the crew and dispatch.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
            {pickerLoading ? (
              <p className="text-sm text-slate-400 py-6 text-center">Loading live projects…</p>
            ) : pickerProjects.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center" data-testid="crew-picker-empty">
                No live projects. Use a standalone job below.
              </p>
            ) : (
              <div className="space-y-2">
                {pickerProjects.map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-200 overflow-hidden"
                    data-testid={`crew-picker-project-${p.id}`}>
                    <div className="flex items-start justify-between gap-3 p-3 bg-slate-50">
                      <button className="text-left min-w-0"
                        onClick={() => setExpandedProject(expandedProject === p.id ? null : p.id)}
                        data-testid={`crew-picker-expand-${p.id}`}>
                        <p className="text-sm font-bold text-slate-900">{p.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {p.site || p.hospital_name || "—"}
                          {p.department ? ` · ${p.department}` : ""}
                          {` · ${(p.phases || []).length} open phase${(p.phases || []).length === 1 ? "" : "s"}`}
                          {p.scheduled_jobs ? ` · ${p.scheduled_jobs} job(s) already scheduled` : ""}
                        </p>
                      </button>
                      <Button size="sm" variant="outline" className="h-8 text-xs shrink-0"
                        onClick={() => startFromProject(p)}
                        data-testid={`crew-picker-pick-project-${p.id}`}>
                        Use project
                      </Button>
                    </div>
                    {expandedProject === p.id && (
                      <div className="divide-y divide-slate-100">
                        {(p.phases || []).length === 0 ? (
                          <p className="text-[12px] text-slate-400 p-3">
                            No open phases — schedule against the project itself.
                          </p>
                        ) : p.phases.map((ph) => (
                          <div key={ph.id} className="flex items-center justify-between gap-3 p-3">
                            <div className="min-w-0">
                              <p className="text-[13px] font-semibold text-slate-800 truncate">{ph.name}</p>
                              <p className="text-[11px] text-slate-500">
                                {(ph.start_date || "").slice(0, 10) || "TBD"}
                                {ph.end_date ? ` — ${ph.end_date.slice(0, 10)}` : ""}
                                {` · ${(ph.status || "upcoming").replace(/_/g, " ")}`}
                                {ph.owner_name ? ` · ${ph.owner_name}` : ""}
                              </p>
                            </div>
                            <Button size="sm" className="h-8 text-xs shrink-0 bg-blue-600 hover:bg-blue-700"
                              onClick={() => startFromProject(p, ph)}
                              data-testid={`crew-picker-pick-phase-${ph.id}`}>
                              Schedule this phase
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="justify-between sm:justify-between">
            <Button variant="ghost" size="sm" onClick={startStandalone}
              data-testid="crew-picker-standalone">
              Standalone job (no project)
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPicker(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job dialog */}
      <Dialog open={!!job} onOpenChange={(o) => !o && setJob(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit job" : "Schedule a job"}</DialogTitle>
            <DialogDescription>
              Who is going, where, and when. Dispatch sends each assignee the job card by email.
            </DialogDescription>
          </DialogHeader>
          {job && (
            <div className="space-y-3">
              <div>
                <Label>Job title</Label>
                <Input value={job.title} placeholder="Isolation room ceiling demo — night shift"
                  onChange={(e) => setJob({ ...job, title: e.target.value })}
                  data-testid="crew-job-title-input" />
              </div>
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={job.job_date}
                    onChange={(e) => setJob({ ...job, job_date: e.target.value })}
                    data-testid="crew-job-date-input" />
                </div>
                <div>
                  <Label>Start</Label>
                  <Input type="time" value={job.start_time}
                    onChange={(e) => setJob({ ...job, start_time: e.target.value })} />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="time" value={job.end_time}
                    onChange={(e) => setJob({ ...job, end_time: e.target.value })} />
                </div>
                <div>
                  <Label>Trade</Label>
                  <Select value={job.trade} onValueChange={(v) => setJob({ ...job, trade: v })}>
                    <SelectTrigger data-testid="crew-job-trade-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {trades.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>Project</Label>
                  <Select value={job.project_id || "none"}
                    onValueChange={(v) => {
                      const p = projects.find((x) => x.id === v);
                      setJob({
                        ...job,
                        project_id: v === "none" ? "" : v,
                        project_name: p?.name || "",
                        location: job.location || p?.facility || p?.location || "",
                      });
                    }}>
                    <SelectTrigger data-testid="crew-job-project-select"><SelectValue placeholder="Standalone job" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Standalone job (no project)</SelectItem>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ICRA permit no.</Label>
                  <Input value={job.icra_permit_number} placeholder="ICRA-001"
                    onChange={(e) => setJob({ ...job, icra_permit_number: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Location / meeting point</Label>
                <Input value={job.location} placeholder="LBJ Main, level 2 — meet at loading dock"
                  onChange={(e) => setJob({ ...job, location: e.target.value })} />
              </div>
              <div>
                <Label>Assign crew</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="crew-assignee-picker">
                  {roster.map((p) => {
                    const on = (job.assignees || []).some((a) => a.id === p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => toggleAssignee(p)}
                        className={`text-[11px] font-bold px-2.5 py-1.5 rounded-full ring-1 transition-colors ${
                          on ? "bg-blue-600 text-white ring-blue-600"
                             : "bg-white text-slate-700 ring-slate-300 hover:ring-blue-400"}`}
                        data-testid={`crew-pick-${p.id}`}>
                        {p.name}
                        <span className="opacity-70"> · {(p.trade || "").replace(/_/g, " ")}</span>
                      </button>
                    );
                  })}
                </div>
                {(job.assignees || []).length === 0 && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Nobody assigned yet — the job will sit in the Unassigned row.
                  </p>
                )}
              </div>
              <div>
                <Label>Scope for the crew</Label>
                <Textarea rows={3} value={job.scope}
                  placeholder="Pull ceiling grid rooms 2-114 to 2-118. Negative air stays running. Debris out via service elevator 3."
                  onChange={(e) => setJob({ ...job, scope: e.target.value })}
                  data-testid="crew-job-scope-input" />
              </div>
              {editingId && (
                <div>
                  <Label>Status</Label>
                  <Select value={job.status || "scheduled"}
                    onValueChange={(v) => setJob({ ...job, status: v })}>
                    <SelectTrigger data-testid="crew-job-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["scheduled", "dispatched", "in_progress", "completed", "cancelled"].map((s) => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setJob(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={saveJob} disabled={saving}
              data-testid="crew-job-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roster dialog */}
      <Dialog open={showRoster} onOpenChange={setShowRoster}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crew roster</DialogTitle>
            <DialogDescription>
              Your own field staff. Trade-contractor portal users appear on the board automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button size="sm" variant="outline"
              onClick={() => { setEditingMemberId(null); setMember(blankMember()); }}
              data-testid="new-crew-member-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add crew member
            </Button>
          </div>
          {member && (
            <div className="rounded-lg border border-blue-300 bg-blue-50/40 p-3 space-y-2">
              <div className="grid sm:grid-cols-3 gap-2">
                <Input placeholder="Full name" value={member.name}
                  onChange={(e) => setMember({ ...member, name: e.target.value })}
                  data-testid="crew-member-name-input" />
                <Select value={member.trade} onValueChange={(v) => setMember({ ...member, trade: v })}>
                  <SelectTrigger data-testid="crew-member-trade-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {trades.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Role (foreman, tech…)" value={member.role}
                  onChange={(e) => setMember({ ...member, role: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                <Input placeholder="Email (for dispatch)" value={member.email}
                  onChange={(e) => setMember({ ...member, email: e.target.value })}
                  data-testid="crew-member-email-input" />
                <Input placeholder="Phone" value={member.phone}
                  onChange={(e) => setMember({ ...member, phone: e.target.value })} />
                <Input placeholder="Certifications" value={member.certifications}
                  onChange={(e) => setMember({ ...member, certifications: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setMember(null)}>Cancel</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={saveMember} disabled={saving} data-testid="crew-member-save-btn">
                  Save crew member
                </Button>
              </div>
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {crew.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No internal crew on the roster yet.</p>
            ) : crew.map((m) => (
              <div key={m.id} className="py-2 flex items-center justify-between gap-3"
                data-testid={`crew-member-row-${m.id}`}>
                <div>
                  <p className="text-sm font-bold text-slate-900">{m.name}
                    {m.active === false ? <span className="ml-2 text-[10px] font-black uppercase text-slate-400">inactive</span> : null}
                  </p>
                  <p className="text-xs text-slate-500">
                    {[(m.trade || "").replace(/_/g, " "), m.role, m.email, m.phone].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="text-slate-600 hover:bg-slate-100"
                    onClick={() => { setEditingMemberId(m.id); setMember({ ...blankMember(), ...m }); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                    onClick={() => removeMember(m)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoster(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
